import { Paper, Tooltip, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import Flex from 'ui/Flex/Flex';
import InfoIcon from '@mui/icons-material/Info';
import StyKeyIndicatorsSubtitle from 'routes/HumanResourcesModule/components/ui/StyKeyIndicatorsSubtitle';
import StyKeyIndicatorsTitle from 'routes/HumanResourcesModule/components/ui/StyKeyIndicatorsTitle';
import { SimulationDateString } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { formatDate } from 'helpers/dateHelper';
import { keyIndicatorsCurrencyFormatter } from 'routes/HumanResourcesModule/helpers/formatHelpers';
import { Trans, useTranslation } from 'react-i18next';
import VariationItemLoader from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/components/KeysIndicators/components/VariationItemLoader';
import { variationType } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/components/KeysIndicators/helpers/types';
import { formatPercentageNumber } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/components/KeysIndicators/helpers/dataHelpers';
import { formatSimulationDateString } from 'routes/HumanResourcesModule/Simulation/helpers/dataHelpers';

type Props = {
    item: string;
    startDate: SimulationDateString;
    year: number; // year of the data passed in props
    isLoading: boolean;
    variation: variationType;
    value: number;
    valueToCompare: number;
};

const StyPaper = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.primary.background,
    height: '100%',
    textAlign: 'left',
    padding: '0 1rem',
    width: '100%',
    display: 'flex',
    flexGrow: '1',
}));
// The variationItem compares the 2 values passed in props.
// An example of application would be the comparison between :
// value : simulated data retrieved from n
// valueToCompare : realized data retrieved from n-1

const VariationItem = ({
    item,
    year,
    startDate,
    isLoading,
    variation,
    value,
    valueToCompare,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isPastYear = new Date().getFullYear() > year;

    const simulationStartDate = new Date(startDate);
    const variationPercentage =
        value > 0 ? ((value - valueToCompare) * 100) / valueToCompare : 0;

    return (
        <StyPaper>
            <Flex
                justifyContent="space-between"
                direction="column"
                sx={{ padding: '1rem 0', width: '100%' }}
            >
                <StyKeyIndicatorsTitle>
                    <Trans
                        i18nKey={`humanResources.simulation.keysIndicators.${item}.${variation}Variation`}
                        tOptions={{
                            year,
                            lastYear: year - 1,
                        }}
                        components={{
                            span: (
                                <span
                                    style={{
                                        color: theme.palette.primary.main,
                                    }}
                                />
                            ),
                        }}
                    />
                    {((!isPastYear &&
                        variation !== variationType.PAST_LAST_YEAR) ||
                        variation === variationType.INITIAL) && (
                        <Tooltip
                            title={t(
                                `humanResources.simulation.keysIndicators.${
                                    variation !== variationType.INITIAL // to display the correct tooltip
                                        ? `${item}.${variation}`
                                        : `${item}.${variationType.INITIAL}`
                                }Tooltip`,
                                {
                                    lastYear: year - 1,
                                    year: year,
                                    startDate: formatDate(simulationStartDate),
                                    endDate: formatSimulationDateString(
                                        `${year}-12-31`,
                                    ),
                                    lastEndDate: formatSimulationDateString(
                                        `${year - 1}-12-31`,
                                    ),
                                },
                            )}
                            key="button-element"
                        >
                            <InfoIcon
                                color="primary"
                                sx={{ paddingLeft: '1px', fontSize: 16 }}
                            />
                        </Tooltip>
                    )}
                </StyKeyIndicatorsTitle>

                <Flex justifyContent="space-between" sx={{ width: '100%' }}>
                    {isLoading ? (
                        <VariationItemLoader />
                    ) : (
                        <>
                            <Typography variant="h5">
                                {variationPercentage === Infinity
                                    ? '∞'
                                    : formatPercentageNumber(
                                          variationPercentage,
                                      )}
                            </Typography>

                            <StyKeyIndicatorsSubtitle>
                                {t(
                                    `humanResources.simulation.keysIndicators.hrChart.soit`,
                                    {
                                        amount: keyIndicatorsCurrencyFormatter(
                                            value - valueToCompare,
                                        ),
                                    },
                                )}
                            </StyKeyIndicatorsSubtitle>
                        </>
                    )}
                </Flex>
            </Flex>
        </StyPaper>
    );
};
export default VariationItem;
